import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Database, RefreshCw, Search, AlertCircle, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { getUserConnections, getProviderLogoUrl, getStatusColor } from '@/lib/integrations';
import type { UserConnection } from '@/lib/integrations/types';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_dashboard/data/')({
  component: DataLandingPage,
});

function DataLandingPage() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<UserConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    async function fetchConnections() {
      try {
        const data = await getUserConnections({ status: 'active' });
        setConnections(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch connections'));
      } finally {
        setLoading(false);
      }
    }
    fetchConnections();
  }, []);

  const filteredConnections = connections.filter((conn) => {
    if (!searchValue.trim()) return true;
    const search = searchValue.toLowerCase();
    return (
      conn.alias.toLowerCase().includes(search) ||
      conn.provider_config_key.toLowerCase().includes(search) ||
      (conn.display_name?.toLowerCase().includes(search) ?? false)
    );
  });

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const data = await getUserConnections({ status: 'active' });
      setConnections(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to refresh'));
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionClick = (alias: string) => {
    navigate({ to: `/data/${alias}` });
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Database className="size-6" />
          <h1 className="text-2xl font-bold">Data Browser</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-32 mb-1" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <AlertCircle className="size-12 text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">Failed to load connections</h2>
          <p className="text-muted-foreground mb-4">{error.message}</p>
          <Button onClick={handleRefresh}>
            <RefreshCw className="size-4 mr-2" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="size-6" />
          <h1 className="text-2xl font-bold">Data Browser</h1>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="size-4 mr-2" />
          Refresh
        </Button>
      </div>

      <p className="text-muted-foreground">
        Select a connection to explore available API endpoints and browse data.
      </p>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search connections..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Empty state */}
      {connections.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center">
            <Database className="size-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No active connections</h3>
            <p className="text-muted-foreground mb-4">
              Connect an integration to start browsing data.
            </p>
            <Button onClick={() => navigate({ to: '/integrations/add' })}>
              Add Integration
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Connections grid */}
      {filteredConnections.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredConnections.map((connection) => {
            const provider = connection.provider_config_key;
            const statusColorClass = getStatusColor(connection.status);

            return (
              <Card
                key={connection.alias}
                className={cn(
                  'cursor-pointer transition-all hover:shadow-md hover:border-primary/50',
                  'group'
                )}
                onClick={() => handleConnectionClick(connection.alias)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src={getProviderLogoUrl(provider)}
                        alt={provider}
                        className="size-10 rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <div
                        className={cn(
                          'absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-background',
                          statusColorClass
                        )}
                        title={connection.status}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate group-hover:text-primary transition-colors">
                        {connection.display_name || provider}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {connection.alias}
                      </div>
                    </div>
                    <ChevronRight className="size-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* No results */}
      {connections.length > 0 && filteredConnections.length === 0 && (
        <div className="text-center py-10 text-muted-foreground">
          No connections match your search.
        </div>
      )}
    </div>
  );
}
